import { faUser } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useProducts } from '@modules/Locations/hooks/useProducts';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Product } from '@shared/components/ui/Product';
import { usePermissions } from '@shared/hooks/usePermissions';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pack } from '../../models/Pack';

interface PackLine {
  product_id: number;
  quantity: number;
  product_name?: string;
  product_code?: string;
  product_image?: string;
}

// 🎯 Extender la interfaz del ref para incluir método personalizado
interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithPack: (pack: Pack) => void;
}

const ViewPackModal = forwardRef<ExtendedModalCenterRef, object>((_props, ref) => {
  // 🎯 Hook de traducciones
  const { t } = useTranslation('packsModule');
  const { products } = useProducts({ limit: 10000, fields: 'basic' });
  const { canAccess } = usePermissions();

  // 🎯 Verificar si el usuario es tenant_3pl
  const isTenant3pl = canAccess(['tenant_3pl']);

  // 🎯 Referencia interna al ModalCenter para evitar recursión
  const internalModalRef = useRef<ModalCenterRef>(null);

  // 🎯 Estado interno del modal - NO causa re-render del padre
  const [pack, setPack] = useState<Pack | null>(null);
  const [lines, setLines] = useState<PackLine[]>([]);

  // 🎯 Efecto para cargar datos del pack
  useEffect(() => {
    if (pack) {
      // Cargar líneas del pack desde la última versión
      const latestVersion = pack.versions[pack.versions.length - 1];
      if (latestVersion?.lines) {
        const packLines: PackLine[] = latestVersion.lines.map((line) => {
          const fullProduct = products.find((p) => p.id === line.product_id);
          return {
            product_id: line.product_id,
            quantity: line.quantity,
            product_name: fullProduct?.name || line.product?.name || `Producto ${line.product_id}`,
            product_code: fullProduct?.code || line.product?.code,
            product_image: fullProduct?.image_hash || line.product?.image_hash || '',
          };
        });
        setLines(packLines);
      } else {
        setLines([]);
      }
    }
  }, [pack, products]);

  const resetForm = () => {
    setPack(null);
    setLines([]);
  };

  const handleClose = () => {
    internalModalRef.current?.closeModal();
    resetForm();
  };

  // 🎯 ÚNICO useCallback necesario - Se pasa a useImperativeHandle
  const openWithPack = useCallback((packData: Pack) => {
    setPack(packData);
    internalModalRef.current?.openModal();
  }, []);

  // 🎯 Exponer métodos personalizados usando useImperativeHandle
  useImperativeHandle(
    ref,
    () => ({
      openModal: () => internalModalRef.current?.openModal(),
      closeModal: () => internalModalRef.current?.closeModal(),
      openWithPack, // ← Método nuevo para uso directo
    }),
    [openWithPack],
  );

  return (
    <ModalCenter
      ref={internalModalRef}
      title={`${pack?.title}`}
      type="info"
      width="xl"
      className="h-[calc(100%-30px)] sm:h-auto"
      onCloseModal={handleClose}
    >
      <div className="space-y-4 flex flex-col justify-between h-full">
        <div className="">
          {/* Mostrar team si es tenant_3pl - arriba */}
          {isTenant3pl && pack?.team?.name && (
            <div className="flex items-center text-sm pb-3 mb-2 border-b border-card-border">
              <FontAwesomeIcon icon={faUser} className="mr-2" size="lg" />
              <span className="text-complementary-text text-xs">{pack.team.name}</span>
            </div>
          )}

          {/* Lista de productos */}
          {lines.length > 0 ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground">
                  {t('card.products_count', {
                    count: lines.length,
                    plural: lines.length !== 1 ? 's' : '',
                  })}
                </span>
              </div>

              <div className=" max-h-80 overflow-y-auto">
                {lines.map((line, index) => (
                  <div
                    key={`${line.product_id}-${index}`}
                    className="flex items-center gap-4 border-t py-4 border-card-border"
                  >
                    {/* Componente Product - 4/5 del espacio */}
                    <div className="flex-[4]">
                      <Product
                        id={line.product_id}
                        name={line.product_name || `Producto ${line.product_id}`}
                        code={line.product_code}
                        src={line.product_image || ''}
                        acronym={line.product_name
                          ?.split(' ')
                          .map((word) => word[0])
                          .join('')
                          .toUpperCase()
                          .slice(0, 2)}
                        enableLineClamp={true}
                        data-testid={`pack-product-${index}`}
                      />
                    </div>

                    {/* Información de cantidad - 1/5 del espacio */}
                    <div className="flex-[1] flex flex-col items-end">
                      <div className="font-medium ml-2 text-sm bg-card-border text-text px-3 py-1 rounded-full">
                        {line.quantity}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground text-sm">📦 {t('card.no_products')}</p>
            </div>
          )}
        </div>
      </div>
    </ModalCenter>
  );
});

export default ViewPackModal;
ViewPackModal.displayName = 'ViewPackModal';
